/**
 * Shopify Custom Wishlist Plugin v2
 * Adds: counter badge, wishlist page support
 */
(function () {
  'use strict';

  const cfg = window.WishlistConfig || {};
  const API_URL    = (cfg.apiUrl    || '').replace(/\/$/, '');
  const API_SECRET = cfg.apiSecret  || '';
  const CURRENCY   = cfg.currency   || '₹';

  if (!API_URL) {
    console.warn('[Wishlist] WishlistConfig.apiUrl is not set. Plugin disabled.');
    return;
  }

  const LS_NAME_KEY  = 'wl_name';
  const LS_PHONE_KEY = 'wl_phone';
  const LS_ITEMS_KEY = 'wl_items';
  const LS_DATA_KEY  = 'wl_data';

  // ── Local state helpers ─────────────────────────────────
  function getName()    { return localStorage.getItem(LS_NAME_KEY)  || ''; }
  function saveName(n)  { localStorage.setItem(LS_NAME_KEY, n); }
  function getPhone()   { return localStorage.getItem(LS_PHONE_KEY) || ''; }
  function savePhone(p) { localStorage.setItem(LS_PHONE_KEY, p); }

  function getLocalItems() {
    try { return JSON.parse(localStorage.getItem(LS_ITEMS_KEY) || '{}'); }
    catch { return {}; }
  }
  function setLocalItem(id, val) {
    const items = getLocalItems();
    if (val) items[String(id)] = true;
    else delete items[String(id)];
    localStorage.setItem(LS_ITEMS_KEY, JSON.stringify(items));
    updateCounter();
  }
  function isWishlisted(id) { return !!getLocalItems()[String(id)]; }

  function getLocalData() {
    try { return JSON.parse(localStorage.getItem(LS_DATA_KEY) || '{}'); }
    catch { return {}; }
  }
  function saveLocalData(productData) {
    const data = getLocalData();
    data[String(productData.product_id)] = productData;
    localStorage.setItem(LS_DATA_KEY, JSON.stringify(data));
  }
  function removeLocalData(productId) {
    const data = getLocalData();
    delete data[String(productId)];
    localStorage.setItem(LS_DATA_KEY, JSON.stringify(data));
  }

  // ── API calls ───────────────────────────────────────────
  async function apiCall(method, body) {
    const opts = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'x-wishlist-secret': API_SECRET,
      },
    };
    if (body && method !== 'GET' && method !== 'HEAD') {
      opts.body = JSON.stringify(body);
    }
    const url = method === 'GET'
      ? `${API_URL}/api/wishlist?phone=${encodeURIComponent(body.phone)}`
      : `${API_URL}/api/wishlist`;
    const res = await fetch(url, opts);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${res.status}`);
    }
    return res.json();
  }

  async function apiAdd(phone, productData)   { return apiCall('POST',   { phone, ...productData }); }
  async function apiRemove(phone, product_id) { return apiCall('DELETE', { phone, product_id }); }
  async function apiGet(phone)                { return apiCall('GET',    { phone }); }

  // ── Sync from server ────────────────────────────────────
  async function syncFromServer() {
    const phone = getPhone();
    updateCounter();
    if (!phone) return;

    try {
      const data = await apiGet(phone);
      const serverItems = data.wishlist || [];

      if (serverItems.length > 0) {
        const fresh = {};
        const dataCache = {};
        serverItems.forEach(entry => {
          fresh[entry.product_id] = true;
          dataCache[entry.product_id] = {
            product_id:       entry.product_id,
            product_title:    entry.product_title,
            product_handle:   entry.product_handle,
            variant_id:       entry.variant_id,
            product_image:    entry.product_image,
            product_price:    entry.product_price,
            compare_at_price: entry.compare_at_price || '',
            added_at:         entry.added_at,
          };
        });
        localStorage.setItem(LS_ITEMS_KEY, JSON.stringify(fresh));
        localStorage.setItem(LS_DATA_KEY, JSON.stringify(dataCache));
      }

      updateAllButtons();
      updateCounter();

      if (window.WishlistPage && typeof window.WishlistPage.refresh === 'function') {
        window.WishlistPage.refresh();
      }
    } catch (e) {
      console.warn('[Wishlist] Sync failed:', e.message);
      updateCounter();
    }
  }

  // ── Counter (badge in header) ───────────────────────────
  function updateCounter() {
    const items = getLocalItems();
    const count = Object.keys(items).length;

    document.querySelectorAll('[data-wishlist-count]').forEach(el => {
      el.textContent = count;
      if (count > 0) {
        el.style.cssText = 'display: flex !important; align-items: center; justify-content: center;';
        el.classList.add('is-visible');
        el.setAttribute('data-count', count);
      } else {
        el.style.cssText = 'display: none !important;';
        el.classList.remove('is-visible');
        el.removeAttribute('data-count');
      }
    });

    document.querySelectorAll('[data-wishlist-link]').forEach(el => {
      el.classList.toggle('has-items', count > 0);
    });
  }

  // ── Heart SVG state ─────────────────────────────────────
  function updateHeartSVG(btn) {
    const active = btn.classList.contains('is-wishlisted');
    btn.querySelectorAll('svg, svg g, svg path').forEach(el => {
      el.removeAttribute('style');
      if (active) {
        el.style.fill        = '#e63946';
        el.style.stroke      = '#e63946';
        el.style.strokeWidth = '2';
        el.style.opacity     = '1';
      } else {
        el.style.fill        = 'none';
        el.style.stroke      = '#333';
        el.style.strokeWidth = '4';
        el.style.opacity     = '1';
      }
    });
  }

  // ── Button state ────────────────────────────────────────
  function updateAllButtons() {
    document.querySelectorAll('.wishlist-btn[data-product-id]').forEach(btn => {
      const id     = btn.dataset.productId;
      const active = isWishlisted(id);
      btn.classList.toggle('is-wishlisted', active);
      btn.setAttribute('aria-label',   active ? 'Remove from wishlist' : 'Add to wishlist');
      btn.setAttribute('aria-pressed', String(active));
      updateHeartSVG(btn);
    });
  }

  function setButtonLoading(btn, loading) {
    btn.disabled = loading;
    if (loading) btn.classList.add('wl-loading');
    else btn.classList.remove('wl-loading');
  }

  // ── Toast ───────────────────────────────────────────────
  let toastTimer = null;
  function showToast(msg, type = 'success') {
    let toast = document.getElementById('wl-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'wl-toast';
      toast.className = 'wl-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.className = `wl-toast is-${type}`;
    void toast.offsetHeight;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 2800);
  }

  // ── Modal ───────────────────────────────────────────────
  let pendingProductData = null;
  let pendingButton      = null;

  function openModal(btn, productData) {
    pendingProductData = productData;
    pendingButton      = btn;
    const overlay = document.getElementById('wl-modal-overlay');
    if (!overlay) return;

    const nameEl  = overlay.querySelector('.wl-modal__product-name');
    const priceEl = overlay.querySelector('.wl-modal__product-price');
    const imgEl   = overlay.querySelector('.wl-modal__product-img');
    const phEl    = overlay.querySelector('.wl-modal__product-img-placeholder');

    if (nameEl)  nameEl.textContent  = productData.product_title || '';
    if (priceEl) priceEl.textContent = productData.product_price ? `${CURRENCY}${productData.product_price}` : '';

    if (productData.product_image) {
      if (imgEl) { imgEl.src = productData.product_image; imgEl.style.display = 'block'; }
      if (phEl)   phEl.style.display = 'none';
    } else {
      if (imgEl)  imgEl.style.display = 'none';
      if (phEl)   phEl.style.display = 'flex';
    }

    const nameInput = overlay.querySelector('#wl-name-input');
    if (nameInput && getName()) nameInput.value = getName();

    const contactInput = overlay.querySelector('#wl-contact-input');
    if (contactInput && getPhone()) contactInput.value = getPhone();

    showModalForm(overlay);
    clearAllErrors(overlay);
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    // Focus name if empty, otherwise focus contact
    if (nameInput && !nameInput.value) {
      setTimeout(() => nameInput.focus(), 250);
    } else if (contactInput) {
      setTimeout(() => contactInput.focus(), 250);
    }
  }

  function closeModal() {
    const overlay = document.getElementById('wl-modal-overlay');
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    pendingProductData = null;
    pendingButton      = null;
  }

  function showModalForm(overlay) {
    overlay.querySelector('.wl-modal__form-section').style.display = 'block';
    overlay.querySelector('.wl-modal__success').classList.remove('is-visible');
  }
  function showModalSuccess(overlay) {
    overlay.querySelector('.wl-modal__form-section').style.display = 'none';
    overlay.querySelector('.wl-modal__success').classList.add('is-visible');
    setTimeout(() => closeModal(), 1800);
  }

  // ── Error helpers ───────────────────────────────────────
  function showFieldError(overlay, inputSelector, errorSelector, msg) {
    const errEl = overlay.querySelector(errorSelector);
    const input = overlay.querySelector(inputSelector);
    if (errEl) { errEl.textContent = msg; errEl.classList.add('is-visible'); }
    if (input) input.classList.add('has-error');
  }

  function clearFieldError(overlay, inputSelector, errorSelector) {
    const errEl = overlay.querySelector(errorSelector);
    const input = overlay.querySelector(inputSelector);
    if (errEl) { errEl.textContent = ''; errEl.classList.remove('is-visible'); }
    if (input) input.classList.remove('has-error');
  }

  function clearAllErrors(overlay) {
    clearFieldError(overlay, '#wl-name-input',    '#wl-name-error');
    clearFieldError(overlay, '#wl-contact-input', '#wl-phone-error');
  }

  // ── Validation ──────────────────────────────────────────
  function validateContact(value) {
    const v = value.trim();
    const digits = v.replace(/\D/g, '');

    // Must be 10 to 15 digits (supports country codes like 91XXXXXXXXXX)
    if (digits.length >= 10 && digits.length <= 15) return null;

    return 'Please enter your phone number with country code (e.g. 91XXXXXXXXXX).';
  }

  async function submitModal() {
    const overlay      = document.getElementById('wl-modal-overlay');
    const contactInput = overlay.querySelector('#wl-contact-input');
    const nameInput    = overlay.querySelector('#wl-name-input');
    const btn          = overlay.querySelector('#wl-modal-submit');
    if (!contactInput || !overlay) return;

    const contact = contactInput.value.trim();
    const name    = nameInput ? nameInput.value.trim() : '';

    // Clear all errors first
    clearAllErrors(overlay);

    // Validate both fields — show errors on both if needed, then stop
    let hasError = false;

    if (!name) {
      showFieldError(overlay, '#wl-name-input', '#wl-name-error', 'Please enter your name.');
      hasError = true;
    }

    const contactErr = validateContact(contact);
    if (contactErr) {
      showFieldError(overlay, '#wl-contact-input', '#wl-phone-error', contactErr);
      hasError = true;
    }

    if (hasError) {
      // Focus the first field with an error
      if (!name && nameInput) nameInput.focus();
      else if (contactErr && contactInput) contactInput.focus();
      return;
    }

    btn.disabled    = true;
    btn.textContent = 'Saving...';

    try {
      const dataToSend = { ...pendingProductData, customer_name: name };
      await apiAdd(contact, dataToSend);
      savePhone(contact);
      saveName(name);
      setLocalItem(pendingProductData.product_id, true);
      saveLocalData(pendingProductData);
      updateAllButtons();
      if (pendingButton) {
        pendingButton.classList.add('wl-animate');
        setTimeout(() => pendingButton.classList.remove('wl-animate'), 400);
      }
      showModalSuccess(overlay);
    } catch (e) {
      showFieldError(overlay, '#wl-contact-input', '#wl-phone-error', 'Something went wrong. Please try again.');
      console.error('[Wishlist] Add failed:', e);
    } finally {
      btn.disabled    = false;
      btn.textContent = 'Save to Wishlist';
    }
  }

  // ── Click handler ───────────────────────────────────────
  async function handleWishlistClick(btn) {
    const productId = btn.dataset.productId;
    if (!productId) return;

    const productData = {
      product_id:       productId,
      product_title:    btn.dataset.productTitle  || '',
      product_handle:   btn.dataset.productHandle || '',
      variant_id:       btn.dataset.variantId     || '',
      product_image:    btn.dataset.productImage  || '',
      product_price:    btn.dataset.productPrice  || '',
      compare_at_price: btn.dataset.comparePrice  || '',
    };

    if (isWishlisted(productId)) {
      const phone = getPhone();
      if (!phone) {
        setLocalItem(productId, false);
        removeLocalData(productId);
        updateAllButtons();
        return;
      }
      setButtonLoading(btn, true);
      try {
        await apiRemove(phone, productId);
        setLocalItem(productId, false);
        removeLocalData(productId);
        updateAllButtons();
        showToast('Removed from wishlist', 'success');
        if (window.WishlistPage && typeof window.WishlistPage.refresh === 'function') {
          window.WishlistPage.refresh();
        }
      } catch (e) {
        showToast('Could not remove. Try again.', 'error');
      } finally {
        setButtonLoading(btn, false);
      }
      return;
    }

    const phone = getPhone();
    if (phone) {
      setButtonLoading(btn, true);
      try {
        const dataToSend = { ...productData, customer_name: getName() };
        await apiAdd(phone, dataToSend);
        setLocalItem(productId, true);
        saveLocalData(productData);
        updateAllButtons();
        btn.classList.add('wl-animate');
        setTimeout(() => btn.classList.remove('wl-animate'), 400);
        showToast('Added to wishlist!', 'success');
      } catch (e) {
        showToast('Could not add to wishlist. Try again.', 'error');
      } finally {
        setButtonLoading(btn, false);
      }
      return;
    }

    openModal(btn, productData);
  }

  function initButtons() {
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('.wishlist-btn[data-product-id]');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        handleWishlistClick(btn);
      }
    });
  }

  function initModal() {
    const overlay = document.getElementById('wl-modal-overlay');
    if (!overlay) return;

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeModal();
    });

    overlay.querySelector('.wl-modal__close')?.addEventListener('click', closeModal);
    overlay.querySelector('#wl-modal-submit')?.addEventListener('click', submitModal);

    // Enter on name field: move to contact if empty, else submit
    overlay.querySelector('#wl-name-input')?.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        const contactInput = overlay.querySelector('#wl-contact-input');
        if (contactInput && !contactInput.value.trim()) {
          contactInput.focus();
        } else {
          submitModal();
        }
      }
    });

    // Enter on contact field: submit
    overlay.querySelector('#wl-contact-input')?.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submitModal(); }
    });

    // Clear individual field errors on input
    overlay.querySelector('#wl-name-input')?.addEventListener('input', function () {
      clearFieldError(overlay, '#wl-name-input', '#wl-name-error');
    });
    overlay.querySelector('#wl-contact-input')?.addEventListener('input', function () {
      clearFieldError(overlay, '#wl-contact-input', '#wl-phone-error');
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeModal();
    });
  }

  // ── Helpers ─────────────────────────────────────────────
  function esc(s) {
    return String(s || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function https(url) {
    if (!url) return '';
    return url.startsWith('//') ? 'https:' + url : url;
  }

  function formatMoney(paise) {
    const amount = paise / 100;
    return '₹' + amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  // ── Wishlist page renderer ──────────────────────────────
  const productCache = {};
  async function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    try {
      const res = await fetch('/products/' + handle + '.js');
      if (!res.ok) return null;
      const p = await res.json();
      productCache[handle] = p;
      return p;
    } catch (e) {
      return null;
    }
  }

  function buildCard(localData, shopifyProduct) {
    if (!shopifyProduct) return '';

    const productId  = localData.product_id;
    const handle     = shopifyProduct.handle;
    const title      = shopifyProduct.title;
    const url        = '/products/' + handle;
    const vendor     = shopifyProduct.vendor || '';
    const available  = shopifyProduct.available;

    const savedVariantId = String(localData.variant_id || '');
    const variants       = shopifyProduct.variants || [];
    const variant        = variants.find(v => String(v.id) === savedVariantId) || variants[0] || {};

    const price        = variant.price            || 0;
    const comparePrice = variant.compare_at_price || 0;
    const onSale       = comparePrice > price && price > 0;
    const pct          = onSale ? Math.round((comparePrice - price) / comparePrice * 100) : 0;

    const imgSrc  = https(localData.product_image || (shopifyProduct.images && shopifyProduct.images[0] && shopifyProduct.images[0].src) || '');
    const wlPrice = (price / 100).toFixed(0);
    const wlCmp   = (comparePrice / 100).toFixed(0);

    return `
    <div class="grid__item grid-product small--one-half medium-up--one-third" data-aos="row-of-3" data-product-handle="${esc(handle)}" data-product-id="${esc(productId)}">
      <div class="grid-product__content">

        ${!available
          ? `<div class="grid-product__tag grid-product__tag--sold-out">Sold Out</div>`
          : onSale
            ? `<div class="grid-product__tag grid-product__tag--sale">Sale</div>`
            : ''}

        <div class="grid__item-image-wrapper">
          <div class="grid-product__image-mask">

            <button
              class="wishlist-btn wishlist-btn--card is-wishlisted"
              type="button"
              aria-label="Remove from wishlist"
              aria-pressed="true"
              data-product-id="${esc(productId)}"
              data-product-title="${esc(title)}"
              data-product-handle="${esc(handle)}"
              data-variant-id="${esc(variant.id || '')}"
              data-product-image="${esc(imgSrc)}"
              data-product-price="${esc(wlPrice)}"
              data-compare-price="${esc(wlCmp)}"
            >
              <svg aria-hidden="true" focusable="false" role="presentation" viewBox="-4 -4 98 93" width="24" height="24">
                <path d="M45 84.334L6.802 46.136C2.416 41.75 0 35.918 0 29.716c0-6.203 2.416-12.034 6.802-16.42 4.386-4.386 10.217-6.802 16.42-6.802 6.203 0 12.034 2.416 16.42 6.802L45 18.654l5.358-5.358c4.386-4.386 10.218-6.802 16.42-6.802 6.203 0 12.034 2.416 16.42 6.802C87.585 17.682 90 23.513 90 29.716c0 6.203-2.415 12.034-6.802 16.42L45 84.334z"
                  fill="none" stroke="white" stroke-width="4" stroke-linejoin="round"/>
              </svg>
            </button>

            <div class="grid__image-ratio grid__image-ratio--square">
              ${imgSrc
                ? `<img class="grid-product__image" src="${esc(imgSrc)}" alt="${esc(title)}" loading="lazy" style="object-fit:cover;width:100%;height:100%;">`
                : `<div class="product-image--placeholder"></div>`}
            </div>
          </div>

          <a href="${esc(url)}" class="grid-product__link">
            <div class="grid-product__meta">
              <div class="grid-product__title">${esc(title)}</div>
              ${vendor ? `<div class="grid-product__vendor">${esc(vendor)}</div>` : ''}
              <div class="grid-product__price">
                ${onSale ? `<span class="grid-product__price--original">${formatMoney(comparePrice)}</span>` : ''}
                <span>${formatMoney(price)}</span>
                ${onSale ? `<span class="grid-product__price--savings">Save ${pct}%</span>` : ''}
              </div>
            </div>
          </a>
        </div>
      </div>

      ${available
        ? `<button class="custom-atc-btn" onclick="addToCartAndOpenGokwik('${esc(variant.id)}', this, 'ADD TO CART')">ADD TO CART</button>`
        : `<button class="custom-atc-btn custom-atc-btn--soldout" disabled>SOLD OUT</button>`}

      <button
        class="custom-atc-btn custom-atc-btn--remove-wl"
        style="background:#fff;color:#111;border:1px solid #111;margin-top:6px;"
        onclick="window.WishlistPlugin && window.WishlistPlugin.removeFromWishlistAndRefresh('${esc(productId)}')"
      >REMOVE FROM WISHLIST</button>
    </div>`;
  }

  async function renderWishlistPage() {
    const container = document.getElementById('wl-product-grid');
    const heading   = document.getElementById('wl-page-heading');
    if (!container) return;

    const items = getLocalItems();
    const data  = getLocalData();
    const ids   = Object.keys(items);

    if (!ids.length) {
      if (heading) heading.textContent = 'My Wishlist — 0 Items';
      container.innerHTML = `
        <div class="grid__item" style="text-align:center;padding:60px 20px;grid-column:1/-1;">
          <h2 style="font-size:20px;margin-bottom:12px;">Your wishlist is empty</h2>
          <p>Save products you love and find them here.</p>
          <a href="/collections/all" class="btn" style="margin-top:16px;display:inline-block;">Browse Products</a>
        </div>`;
      return;
    }

    if (heading) heading.textContent = `My Wishlist — ${ids.length} Item${ids.length === 1 ? '' : 's'}`;

    container.innerHTML = ids.map(() => `
      <div class="grid__item grid-product small--one-half medium-up--one-third" style="opacity:0.4;">
        <div class="grid-product__content">
          <div class="grid__item-image-wrapper">
            <div class="grid__image-ratio grid__image-ratio--square" style="background:#eee;"></div>
          </div>
        </div>
      </div>`).join('');

    const cards = await Promise.all(ids.map(async id => {
      const localData = data[id];
      if (!localData || !localData.product_handle) return '';
      const shopifyProduct = await fetchProduct(localData.product_handle);
      return buildCard(localData, shopifyProduct);
    }));

    container.innerHTML = cards.join('');
    updateAllButtons();
  }

  async function removeFromWishlistAndRefresh(productId) {
    const phone = getPhone();
    if (phone) {
      try { await apiRemove(phone, productId); } catch (e) { /* silent */ }
    }
    setLocalItem(productId, false);
    removeLocalData(productId);
    updateAllButtons();
    showToast('Removed from wishlist', 'success');
    renderWishlistPage();
  }

  function boot() {
    updateAllButtons();
    updateCounter();
    initButtons();
    initModal();
    setInterval(updateCounter, 1000);
    syncFromServer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.WishlistPlugin = {
    updateAllButtons,
    updateCounter,
    openModal,
    getLocalItems,
    getLocalData,
    isWishlisted,
    apiRemove,
    setLocalItem,
    removeLocalData,
    getPhone,
    getName,
    removeFromWishlistAndRefresh,
  };

  window.WishlistPage = {
    refresh: renderWishlistPage,
  };

})();